import { useEffect, useRef, useState, type FormEvent } from 'react';
import { AnimatePresence, motion } from 'motion/react';
import { Check } from 'lucide-react';
import { usePageTitle } from '../lib/usePageTitle';
import { ApiError, publicApi } from '../lib/apiClient';
import './career-restart.css';
import { validateNativeForm } from '../lib/publicFormValidation';

const heroImages = [
  'https://images.unsplash.com/photo-1711182673833-7e11dffa0eec?fm=jpg&q=60&w=3000&auto=format&fit=crop',
  'https://images.unsplash.com/photo-1573496359142-b8d87734a5a2?fm=jpg&q=60&w=3000&auto=format&fit=crop',
  'https://images.unsplash.com/photo-1627161683077-e34782c24d81?fm=jpg&q=60&w=3000&auto=format&fit=crop',
  'https://images.unsplash.com/photo-1614786269829-d24616faf56d?fm=jpg&q=60&w=3000&auto=format&fit=crop',
  'https://images.unsplash.com/photo-1770627016447-cb9d29ed0398?fm=jpg&q=60&w=3000&auto=format&fit=crop',
] as const;

const stories = [
  {
    image:
      'https://images.unsplash.com/photo-1770626894265-bdb99db109f1?fm=jpg&q=60&w=3000&auto=format&fit=crop',
    title: '3-year break, back in Finance',
    role: 'Bangalore · Ops Lead',
    copy: 'Took time off after her second child, came back through the program into a role that actually used her experience.',
  },
  {
    image:
      'https://images.unsplash.com/photo-1600275669439-14e40452d20b?fm=jpg&q=60&w=3000&auto=format&fit=crop',
    title: '5-year break, back in Tech',
    role: 'Chennai · Product Analyst',
    copy: 'Refreshed her tooling knowledge through the program and rebuilt her interview confidence from scratch.',
  },
  {
    image:
      'https://images.unsplash.com/photo-1543269664-56d93c1b41a6?fm=jpg&q=60&w=3000&auto=format&fit=crop',
    title: '2-year break, back in HR',
    role: 'Coimbatore · People Partner',
    copy: 'Restarted closer to where she left off, with placement support that stayed with her through the offer.',
  },
] as const;

const sessionFeatures = [
  {
    title: 'Leader-led restart strategies',
    copy: 'Successful women leaders speak directly to attendees — real advice on how they navigated their own return, not theory.',
  },
  {
    title: 'Role-focused tracks',
    copy: 'Dedicated sessions built around specific job roles and functions women can realistically target after a career break.',
  },
  {
    title: 'Company-partnered programs',
    copy: 'We also run company-specific versions of this program with employers who want to support their own returning talent directly.',
  },
] as const;

const joinSteps = [
  {
    title: 'Register for a Saturday',
    copy: 'Sign up for the next available session — no commitment beyond showing up, 11 AM to 1 PM.',
  },
  {
    title: 'Hear from a woman leader',
    copy: 'A successful woman leader shares her own restart story and practical strategies for coming back.',
  },
  {
    title: 'Go deep on your target role',
    copy: "Join the role-focused portion of the session built around the kind of position you're aiming to return to.",
  },
  {
    title: 'Ongoing placement support',
    copy: "We continue working with you toward an actual role after the session — this isn't the end of the support.",
  },
] as const;

const reveal = {
  initial: { opacity: 0, y: 24 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, amount: 0.18 },
  transition: { duration: 0.7 },
};

const countryCodes = [
  { label: '🇮🇳 +91', value: '+91' },
  { label: '🇺🇸 +1', value: '+1' },
  { label: '🇨🇦 +1', value: '+1-CA' },
  { label: '🇹🇭 +66', value: '+66' },
] as const;

const SessionRegistrationModal = ({ onClose }: { onClose: () => void }) => {
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const submittingRef = useRef(false);
  const firstInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    firstInputRef.current?.focus();

    const closeOnEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && !submittingRef.current) onClose();
    };
    document.addEventListener('keydown', closeOnEscape);

    return () => {
      document.body.style.overflow = previousOverflow;
      document.removeEventListener('keydown', closeOnEscape);
    };
  }, [onClose]);

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (submittingRef.current) return;
    if (
      !validateNativeForm(event.currentTarget, {
        name: [2, 100],
        email: [1, 255],
        phone: [10, 10],
        city: [0, 100],
        breakLength: [0, 50],
        targetRole: [0, 255],
      })
    )
      return;
    const formData = new FormData(event.currentTarget);
    const countryCode = String(formData.get('countryCode') ?? '+91').replace('-CA', '');
    const phoneDigits = String(formData.get('phone') ?? '');
    submittingRef.current = true;
    setSubmitting(true);
    setSubmitError(null);
    try {
      await publicApi.submitCareerRestart({
        name: String(formData.get('name') ?? ''),
        email: String(formData.get('email') ?? ''),
        phone: `${countryCode} ${phoneDigits}`,
        city: String(formData.get('city') ?? ''),
        breakLength: String(formData.get('breakLength') ?? ''),
        targetRole: String(formData.get('targetRole') ?? ''),
      });
      setSubmitted(true);
    } catch (error) {
      setSubmitError(error instanceof ApiError ? error.message : 'Something went wrong. Please try again.');
    } finally {
      submittingRef.current = false;
      setSubmitting(false);
    }
  };

  return (
    <motion.div
      className="restart-modal-overlay"
      role="presentation"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      onMouseDown={(event) => {
        if (event.target === event.currentTarget && !submittingRef.current) onClose();
      }}
    >
      <motion.div
        className="restart-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="restart-modal-title"
        initial={{ opacity: 0, y: 18, scale: 0.98 }}
        animate={{ opacity: 1, y: 0, scale: 1 }}
        exit={{ opacity: 0, y: 12, scale: 0.98 }}
      >
        <button type="button" className="restart-modal-close" onClick={onClose} disabled={submitting} aria-label="Close">
          <span aria-hidden="true">×</span>
        </button>

        {submitted ? (
          <div className="restart-form-success" aria-live="polite">
            <span>
              <Check aria-hidden="true" />
            </span>
            <h3 id="restart-modal-title">You're registered.</h3>
            <p>
              We'll send you the joining details ahead of the next Saturday session, 11 AM – 1 PM.
            </p>
          </div>
        ) : (
          <>
            <span className="restart-modal-eyebrow">// Career Restart</span>
            <h3 id="restart-modal-title">Register for a session</h3>
            <p className="restart-modal-subtitle">
              Every Saturday, 11 AM – 1 PM. Open to women PAN India.
            </p>

            <form className="restart-form" onSubmit={handleSubmit} onInput={(event) => { const field = event.target; if (field instanceof HTMLInputElement || field instanceof HTMLTextAreaElement || field instanceof HTMLSelectElement) field.setCustomValidity(''); }}>
              <div className="restart-form-row">
                <label>
                  Your name
                  <input
                    ref={firstInputRef}
                    name="name"
                    type="text"
                    placeholder="Anjali Mehta"
                    pattern="[A-Za-z\s]+"
                    title="Please enter alphabets only"
                    required
                  />
                </label>
                <label>
                  Email
                  <input name="email" type="email" placeholder="anjali@email.com" required />
                </label>
              </div>
              <div className="restart-form-row">
                <label>
                  Phone
                  <div className="restart-phone-group">
                    <select name="countryCode" defaultValue="+91" aria-label="Country code">
                      {countryCodes.map(({ label, value }) => (
                        <option key={label} value={value}>
                          {label}
                        </option>
                      ))}
                    </select>
                    <input
                      name="phone"
                      type="tel"
                      placeholder="9876543210"
                      inputMode="numeric"
                      pattern="[0-9]{10}"
                      maxLength={10}
                      minLength={10}
                      title="Enter exactly 10 digits"
                      onInput={(event) => {
                        const target = event.currentTarget;
                        target.value = target.value.replace(/\D/g, '').slice(0, 10);
                      }}
                      required
                    />
                  </div>
                </label>
                <label>
                  City
                  <input name="city" type="text" placeholder="Bangalore" />
                </label>
              </div>
              <label>
                Length of your career break
                <select name="breakLength" defaultValue="Less than 1 year">
                  <option>Less than 1 year</option>
                  <option>1–2 years</option>
                  <option>3–5 years</option>
                  <option>5+ years</option>
                </select>
              </label>
              <label>
                <span>
                  What role are you aiming to return to? <small>(optional)</small>
                </span>
                <textarea name="targetRole" placeholder="e.g. Finance, HR, Product, Tech..." />
              </label>
              {submitError && <p role="alert" className="restart-form-note">{submitError}</p>}
              <button type="submit" className="restart-form-submit" disabled={submitting} aria-busy={submitting}>
                {submitting ? 'Submitting…' : 'Register for a session →'}
              </button>
              <p className="restart-form-note">
                We'll send you the joining details ahead of the next Saturday session.
              </p>
            </form>
          </>
        )}
      </motion.div>
    </motion.div>
  );
};

export const CareerRestartPage = () => {
  usePageTitle('Career Restart for Women — QuantumHire');
  const [isRegistrationOpen, setIsRegistrationOpen] = useState(false);

  return (
    <main className="restart-page">
      <header className="restart-hero">
        <div className="restart-hero-inner">
          <div className="restart-hero-copy">
            <div className="restart-eyebrow">
              <span /> QuantumHire Career Restart
            </div>
            <h1>
              Coming back after a break is a <em>restart</em>, not a step down.
            </h1>
            <p>
              A weekly session for women on a career break — every Saturday, 11 AM to 1 PM. Hear
              directly from successful women leaders, get real restart strategies, and explore
              role-focused tracks built for what's next. Open to women PAN India, with
              company-specific programs also available.
            </p>
            <button
              type="button"
              className="restart-primary-button"
              onClick={() => setIsRegistrationOpen(true)}
            >
              Register for a session →
            </button>
          </div>

          <div className="restart-hero-collage" aria-hidden="true">
            {heroImages.map((src, index) => (
              <div key={src} className={`restart-collage-cell restart-collage-${index + 1}`}>
                <img
                  src={src}
                  alt=""
                  loading={index === 0 ? 'eager' : 'lazy'}
                  referrerPolicy="no-referrer"
                />
              </div>
            ))}
          </div>
        </div>
      </header>

      <section className="restart-stats" aria-label="Career Restart program details">
        <div className="restart-stats-grid">
          {[
            ['Saturdays', '11 AM – 1 PM, every week'],
            ['100+', 'Successful career reboots'],
            ['PAN', 'India — open nationwide'],
          ].map(([number, label], index) => (
            <div key={label} className={`restart-stat restart-stat-${index + 1}`}>
              <strong>{number}</strong>
              <span>{label}</span>
            </div>
          ))}
        </div>
      </section>

      <section className="restart-section">
        <div className="restart-section-heading">
          <h2>What every session covers</h2>
          <p>
            Not a generic upskilling webinar — built specifically for the return-to-work transition.
          </p>
        </div>
        <div className="restart-feature-grid">
          {sessionFeatures.map(({ title, copy }, index) => (
            <motion.article
              key={title}
              className={`restart-feature restart-feature-${index + 1}`}
              {...reveal}
              transition={{ duration: 0.7, delay: index * 0.08 }}
            >
              <span>{String(index + 1).padStart(2, '0')}</span>
              <h3>{title}</h3>
              <p>{copy}</p>
            </motion.article>
          ))}
        </div>
      </section>

      <section className="restart-section restart-section-tint">
        <div className="restart-section-heading">
          <h2>The kind of comeback we support</h2>
          <p>
            Different backgrounds, different breaks, different cities — the same restart. (Composite
            examples, not photos of specific alumnae.)
          </p>
        </div>
        <div className="restart-story-grid">
          {stories.map(({ image, title, role, copy }, index) => (
            <motion.article
              key={title}
              className="restart-story"
              {...reveal}
              transition={{ duration: 0.7, delay: index * 0.08 }}
            >
              <div className="restart-story-avatar">
                <img src={image} alt="" loading="lazy" referrerPolicy="no-referrer" />
              </div>
              <h3>{title}</h3>
              <span>{role}</span>
              <p>{copy}</p>
            </motion.article>
          ))}
        </div>
      </section>

      <section className="restart-section">
        <div className="restart-section-heading">
          <h2>How to join a session</h2>
        </div>
        <div className="restart-steps">
          {joinSteps.map(({ title, copy }, index) => (
            <motion.article
              key={title}
              className="restart-step"
              {...reveal}
              transition={{ duration: 0.7, delay: index * 0.06 }}
            >
              <span>{String(index + 1).padStart(2, '0')}</span>
              <div>
                <h3>{title}</h3>
                <p>{copy}</p>
              </div>
            </motion.article>
          ))}
        </div>
      </section>

      <section className="restart-final-cta">
        <h2>Ready to restart?</h2>
        <button
          type="button"
          className="restart-primary-button"
          onClick={() => setIsRegistrationOpen(true)}
        >
          Register for a session →
        </button>
      </section>

      <AnimatePresence>
        {isRegistrationOpen && (
          <SessionRegistrationModal onClose={() => setIsRegistrationOpen(false)} />
        )}
      </AnimatePresence>
    </main>
  );
};